/**
 * Unified map — the pure derivation layer behind every ODL map surface.
 *
 * A map paints sites as colored markers and mirrors them in a searchable
 * sidebar list. This module owns the vocabulary those two surfaces agree on:
 * how a site's raw connection/alarm flags collapse into one of four map
 * statuses, how the client-side status and alarm-category filters
 * narrow the set, how search and name/status sorting order it, and how a marker
 * cluster summarizes the statuses beneath it.
 *
 * Contains:
 * - SITE_STATUS / SORT_FIELD / SORT_DIRECTION / SITE_SEARCH_KEYS — vocab constants
 * - deriveSiteStatus  — raw site flags -> one of the four statuses
 * - hasMaintenance    — does the site carry an unresolved maintenance log
 * - deriveUnitStatus  — raw site flags -> the structures grid's status token
 * - filterSites       — status-toggle + alarm-category client filter
 * - statusFiltersFromSelection — URL status list -> filterSites toggle record
 * - searchSites       — Fuse fuzzy search over name/address/iccid/loads
 * - sortSites         — name or status ordering (asc/desc)
 * - clusterBreakdown  — per-status counts for a cluster's conic-gradient ring
 *
 * Returns plain JS — no JSX, no redux, no Immutable.
 */
import Fuse from 'fuse.js';

export const SITE_STATUS = Object.freeze({
  ALARMING: 'alarming',
  SILENT: 'silent',
  OFFLINE: 'offline',
  ONLINE: 'online',
});

export const SORT_FIELD = Object.freeze({ NAME: 'name', STATUS: 'status' });
export const SORT_DIRECTION = Object.freeze({ ASC: 'asc', DESC: 'desc' });

// Fuse keys — nested `loads.name` searches each load's name (array of objects).
export const SITE_SEARCH_KEYS = Object.freeze(['name', 'address', 'iccid', 'site_group_name', 'loads.name']);

// Worst-first ranking: drives STATUS sort order and the order statuses stack in
// a cluster's conic-gradient ring.
const STATUS_RANK = Object.freeze({
  [SITE_STATUS.ALARMING]: 0,
  [SITE_STATUS.OFFLINE]: 1,
  [SITE_STATUS.SILENT]: 2,
  [SITE_STATUS.ONLINE]: 3,
});

const FUSE_OPTIONS = Object.freeze({ keys: SITE_SEARCH_KEYS, threshold: 0.3, ignoreLocation: true });

function toSet(values) {
  return values instanceof Set ? values : new Set(values ?? []);
}

/**
 * Collapse a site's raw flags into a single map status.
 *
 * Precedence is alarm-first: an alarming site reads as alarming even when it is
 * also offline. Below that the split is between a site ODL has never heard from
 * and one it has stopped hearing from — `silent` covers both a site with no
 * radio and a radio that has never reported, while `offline` is reserved for a
 * site that reported at least once and went quiet. Only the latter is
 * actionable, which is why only the latter is amber.
 *
 * `has_radio` is absent until the detail endpoint ships — a missing value is
 * treated as radio-present so the status degrades to the legacy three-state
 * (alarming / offline / online) model rather than reporting every site silent.
 * `last_communication` is explicitly null for a site that has never reported;
 * an absent key degrades the same way `has_radio` does, to has-reported.
 *
 * @param {object} site - a map site row from the dashboard map endpoint.
 * @returns {string} one of SITE_STATUS.
 */
export function deriveSiteStatus(site) {
  if (site.has_alarm) return SITE_STATUS.ALARMING;
  const hasRadio = site.has_radio ?? true;
  if (!hasRadio) return SITE_STATUS.SILENT;
  if (site.last_communication === null) return SITE_STATUS.SILENT;
  if (!site.is_online) return SITE_STATUS.OFFLINE;
  return SITE_STATUS.ONLINE;
}

/**
 * Does the site carry an unresolved maintenance log? Surfaces as a badge beside
 * the status marker, never as a status of its own. The flag only rides along on
 * endpoints that publish it; absent reads as no outstanding maintenance.
 *
 * @param {object} site
 * @returns {boolean}
 */
export function hasMaintenance(site) {
  return Boolean(site?.has_unresolved_logs);
}

/**
 * Collapse a site's raw flags onto the structures grid's status ladder — the
 * `status` token that grid's rows carry, so the map's list paints the same dot
 * for the same site the grid does.
 *
 * The rungs mirror `SITE_STATUS_BUCKETS`, the backend's own ladder, in its
 * order: an alarm outranks everything, then an unresolved maintenance log, then
 * a site with no linked radio, then a linked radio reporting offline, and
 * otherwise good. `no_radio` asks only whether a radio is linked — a site whose
 * radio has never checked in has one, so it reads `offline`.
 *
 * The map feed publishes no status of its own, only the flags underneath one,
 * which is why this derivation exists rather than a field read. It sits beside
 * `deriveSiteStatus`, which answers a different question — what colour the
 * marker paints — on a four-value vocabulary the map's filters and legend use,
 * and which does treat a radio that has never reported as its own case.
 *
 * @param {object} site - a map site row from the dashboard map endpoint.
 * @returns {string} one of `bad`, `unresolved_maintenance`, `no_radio`, `offline`, `good`.
 */
export function deriveUnitStatus(site) {
  if (site.has_alarm) return 'bad';
  if (hasMaintenance(site)) return 'unresolved_maintenance';
  if (!(site.has_radio ?? true)) return 'no_radio';
  if (!site.is_online) return 'offline';
  return 'good';
}

/**
 * Apply the sidebar's client-side filters: the four status toggles plus the
 * per-alarm-category checklist. A status whose toggle is explicitly `false` is
 * dropped. When categories are selected, they narrow only alarming sites (an
 * alarming site survives if any of its alarms carries a selected category) —
 * non-alarming sites are unaffected, matching the legacy map filter.
 *
 * @param {object[]} sites
 * @param {{ statusFilters?: Record<string, boolean>, selectedCategories?: Iterable<string> }} [options]
 * @returns {object[]} a new filtered array.
 */
export function filterSites(sites, { statusFilters = {}, selectedCategories = [] } = {}) {
  const categories = toSet(selectedCategories);
  return sites.filter(site => {
    const status = deriveSiteStatus(site);
    if (statusFilters[status] === false) return false;
    if (status === SITE_STATUS.ALARMING && categories.size > 0) {
      return (site.alarms ?? []).some(alarm => categories.has(alarm.category_key));
    }
    return true;
  });
}

/**
 * Bridge the map URL's "selected statuses" list to `filterSites`' toggle record.
 * The URL lists what the user chose; an empty list is the unfiltered map, so it
 * yields an empty record rather than a record of falses.
 *
 * @param {Iterable<string>} selectedStatuses
 * @returns {Record<string, boolean>} every unselected status mapped to false.
 */
export function statusFiltersFromSelection(selectedStatuses) {
  const selected = toSet(selectedStatuses);
  if (selected.size === 0) return {};
  return Object.values(SITE_STATUS).reduce(
    (filters, status) => (selected.has(status) ? filters : { ...filters, [status]: false }),
    {},
  );
}

/**
 * Fuzzy-search sites over name, address, ICCID, group name, and load names.
 * An empty/whitespace query returns the input unchanged.
 *
 * @param {object[]} sites
 * @param {string} query
 * @returns {object[]} matching sites in Fuse relevance order.
 */
export function searchSites(sites, query) {
  const trimmed = (query ?? '').trim();
  if (!trimmed) return sites;
  const fuse = new Fuse(sites, FUSE_OPTIONS);
  return fuse.search(trimmed).map(result => result.item);
}

/**
 * Compare two statuses by worst-first rank (alarming < offline < silent <
 * online).
 *
 * @param {string} a
 * @param {string} b
 * @returns {number} negative when `a` is worse (sorts first).
 */
export function compareStatus(a, b) {
  return STATUS_RANK[a] - STATUS_RANK[b];
}

function compareByField(a, b, field) {
  if (field === SORT_FIELD.STATUS) {
    const rankDiff = compareStatus(deriveSiteStatus(a), deriveSiteStatus(b));
    if (rankDiff !== 0) return rankDiff;
  }
  return (a.name ?? '').localeCompare(b.name ?? '');
}

/**
 * Order sites by name or status. Status sorts worst-first (alarming, offline,
 * silent, online) with name as the tiebreak; direction flips the whole order.
 *
 * @param {object[]} sites
 * @param {{ field?: string, direction?: string }} [options]
 * @returns {object[]} a new sorted array.
 */
export function sortSites(sites, { field = SORT_FIELD.NAME, direction = SORT_DIRECTION.ASC } = {}) {
  const sign = direction === SORT_DIRECTION.DESC ? -1 : 1;
  return [...sites].sort((a, b) => sign * compareByField(a, b, field));
}

/**
 * Count how many of the given statuses fall into each of the four buckets, for
 * a cluster marker's conic-gradient ring.
 *
 * @param {string[]} statuses - the status of each marker under the cluster.
 * @returns {{ alarming: number, silent: number, offline: number, online: number }}
 */
export function clusterBreakdown(statuses) {
  const counts = { alarming: 0, silent: 0, offline: 0, online: 0 };
  statuses.forEach(status => {
    if (counts[status] != null) counts[status] += 1;
  });
  return counts;
}
